import { createSlice } from '@reduxjs/toolkit';

const loadInitialState = () => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    const userType = localStorage.getItem('userType');
    const slug = localStorage.getItem('slug');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const is_active = localStorage.getItem('is_active') === 'true';
    const userLoggedIn = localStorage.getItem('user_logged_in') === 'true';

    console.log('🔍 Loading initial state from localStorage:', {
      token: !!token,
      user: !!user,
      userType,
      slug,
      rememberMe,
      is_active,
      userLoggedIn,
    });

    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        console.log('👤 Parsed user:', parsedUser);
        
        // Generate slug fallback more reliably
        let finalSlug = null;
        if (slug && slug !== '' && slug !== 'null' && slug !== 'undefined') {
          finalSlug = slug;
        } else if (parsedUser.slug) {
          finalSlug = parsedUser.slug;
        } else if (parsedUser.id) {
          finalSlug = parsedUser.id.toString();
        }
        
        console.log('🏷️ Final slug resolved:', finalSlug);
        
        // Map availability to isOnline boolean
        const availability = parsedUser.availability || 'Offline';
        const isOnline = availability === 'Available now' || availability === 'Online' || availability === true;
        
        const badgeData = localStorage.getItem('badge');
        let parsedBadge = null;
        if (badgeData) {
          try {
            parsedBadge = JSON.parse(badgeData);
          } catch (error) {
            console.error('Error parsing badge data:', error);
          }
        }

        return {
          user: parsedUser,
          token,
          userType: userType || parsedUser.user_type,
          slug: finalSlug,
          badge: parsedBadge,
          isOnline: isOnline,
          availability: availability,
          rememberMe: rememberMe,
          isLoading: false,
          error: null,
          isAuthenticated: true,
          is_active: parsedUser.is_active ?? (localStorage.getItem('is_active') === 'true' ? "1" : "0"),
          user_logged_in: userLoggedIn || true, // Set to true if token exists
        };
      } catch (error) {
        console.error('❌ Error loading auth state from localStorage:', error);
        // Clear corrupted localStorage data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
        localStorage.removeItem('slug');
        localStorage.removeItem('badge');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('is_active');
        localStorage.removeItem('user_logged_in');
      }
    }
  }

  return {
    user: null,
    token: null,
    userType: null,
    slug: null,
    badge: null,
    isOnline: false,
    availability: 'Offline',
    rememberMe: false,
    isLoading: false,
    error: null,
    isAuthenticated: false,
    is_active: null,
    user_logged_in: false,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: loadInitialState(),
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      console.log('🎉 Login success payload:', action.payload);
      
      state.isLoading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.userType = action.payload.userType || action.payload.user?.user_type;
      state.badge = action.payload.badge || null;
      state.rememberMe = action.payload.rememberMe || false;
      state.is_active = action.payload.user?.is_active ?? null;
      
      // More robust slug generation with debugging
      let slug = null;
      if (action.payload.slug) {
        slug = action.payload.slug;
      } else if (action.payload.user?.slug) {
        slug = action.payload.user.slug;
      } else if (action.payload.user?.id) {
        slug = action.payload.user.id.toString();
      }
      
      console.log('🏷️ Generated slug:', slug);
      
      state.slug = slug;
      
      // Map availability to isOnline boolean
      const availability = action.payload.availability || action.payload.user?.availability || action.payload.u_avail || 'Offline';
      state.isOnline = availability === 'Available now' || availability === 'Online' || availability === true;
      state.availability = availability;
      
      console.log('🔍 Login Success Debug:', {
        'action.payload.u_avail': action.payload.u_avail,
        'action.payload.availability': action.payload.availability,
        'action.payload.user?.availability': action.payload.user?.availability,
        'final availability': availability,
        'isOnline': state.isOnline,
        'rememberMe': state.rememberMe
      });
      state.error = null;
      state.isAuthenticated = true;
      state.user_logged_in = true; // Set login flag

      if (typeof window !== 'undefined') {
        console.log('💾 Saving to localStorage:', {
          token: !!action.payload.token,
          user: !!action.payload.user,
          userType: state.userType,
          slug: slug,
          rememberMe: state.rememberMe,
          mobile_verified_at: action.payload.user?.mobile_verified_at,
          is_active: state.is_active,
        });
        
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        localStorage.setItem('userType', state.userType || '');
        localStorage.setItem('rememberMe', state.rememberMe ? 'true' : 'false');
        localStorage.setItem('user_logged_in', 'true'); // Store login flag
        if (state.badge) {
          localStorage.setItem('badge', JSON.stringify(state.badge));
        }
        if (state.is_active !== null && state.is_active !== undefined) {
          localStorage.setItem('is_active', (state.is_active === "1" || state.is_active === 1) ? 'true' : 'false');
        }
        
        // Only store slug if it exists and is not empty
        if (slug && slug.trim() !== '') {
          localStorage.setItem('slug', slug);
          console.log('✅ Slug saved to localStorage:', slug);
        } else {
          console.warn('⚠️ No valid slug to save');
          localStorage.removeItem('slug'); // Remove any existing slug
        }
      }
    },
    loginFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    setUserType: (state, action) => {
      state.userType = action.payload;
      if (state.user) {
        state.user.user_type = action.payload;
        // Update localStorage as well
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
          localStorage.setItem('userType', action.payload);
        }
      }
    },
    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        };
        if (action.payload.is_active !== undefined) {
          state.is_active = action.payload.is_active;
        }
        // Update localStorage with new user data
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      }
    },
    setAuthLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.userType = null;
      state.slug = null; // Clear slug on logout
      state.badge = null; // Clear badge on logout
      state.rememberMe = false; // Clear remember me on logout
      state.isOnline = false;
      state.availability = 'Offline';
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      state.is_active = null;
      state.user_logged_in = false; // Clear login flag
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
        localStorage.removeItem('slug');
        localStorage.removeItem('badge');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('is_active');
        localStorage.removeItem('user_logged_in'); // Clear login flag
      }
    },
    // Action for when user returns from email verification
    emailVerified: (state) => {
      if (state.user) {
        state.user.email_verified_at = new Date().toISOString();
        // Update localStorage with new user data
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      }
    },
    // Action for when mobile number is verified
    mobileVerified: (state, action) => {
      if (state.user) {
        state.user.mobile_verified_at = action.payload.timestamp || new Date().toISOString();
        // Update localStorage with new user data
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      }
    },
    // Action to update online status
    updateOnlineStatus: (state, action) => {
      state.isOnline = action.payload;
      if (state.user) {
        state.user.is_online = action.payload;
        // Update localStorage with new user data
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      }
    },
    // Action to update availability status
    updateAvailability: (state, action) => {
      state.availability = action.payload;
      // Map availability to isOnline boolean
      state.isOnline = action.payload === 'Available now' || action.payload === 'Online' || action.payload === true;
      
      if (state.user) {
        state.user.availability = action.payload;
        state.user.is_online = state.isOnline;
        // Update localStorage with new user data
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      }
    },
    // Action to restore auth state from localStorage
    restoreAuthState: (state, action) => {
      console.log('🔄 Restoring auth state:', action.payload);
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.userType = action.payload.userType;
      state.is_active = action.payload.is_active ?? null;
      
      // More robust slug restoration
      let slug = null;
      if (action.payload.slug && action.payload.slug !== '' && action.payload.slug !== 'null') {
        slug = action.payload.slug;
      } else if (action.payload.user?.slug) {
        slug = action.payload.user.slug;
      } else if (action.payload.user?.id) {
        slug = action.payload.user.id.toString();
      }
      
      console.log('🏷️ Restored slug:', slug);
      state.slug = slug;
      state.isOnline = action.payload.isOnline || action.payload.user?.is_online || false;
      state.availability = action.payload.availability || action.payload.user?.availability || 'Offline';
      state.isAuthenticated = true;
      state.user_logged_in = true; // Set login flag when restoring auth state
    },
    // Action to update slug from any source (storeterms, profile fetch, etc.)
    updateSlug: (state, action) => {
      console.log('🏷️ Updating slug:', action.payload);
      
      let slug = null;
      // Extract slug from different possible sources
      if (typeof action.payload === 'string') {
        // Direct slug string
        slug = action.payload;
      } else if (action.payload?.u_profile?.fp_slug) {
        // From storeterms response (u_profile.fp_slug)
        slug = action.payload.u_profile.fp_slug;
      } else if (action.payload?.slug) {
        // From user object
        slug = action.payload.slug;
      }
      
      // Only update if we have a valid slug
      if (slug && slug.trim() !== '' && slug !== 'null' && slug !== 'undefined') {
        state.slug = slug;
        
        // Update user object if it exists
        if (state.user) {
          state.user.slug = slug;
          
          // Update localStorage
          if (typeof window !== 'undefined') {
            localStorage.setItem('slug', slug);
            localStorage.setItem('user', JSON.stringify(state.user));
            console.log('✅ Slug updated in Redux and localStorage:', slug);
          }
        }
      } else {
        console.warn('⚠️ Invalid slug provided to updateSlug');
      }
    },
    // Action to set badge
    setBadge: (state, action) => {
      state.badge = action.payload;
      if (typeof window !== 'undefined') {
        if (action.payload) {
          localStorage.setItem('badge', JSON.stringify(action.payload));
        } else {
          localStorage.removeItem('badge');
        }
      }
    },
    // Sync user_logged_in flag from localStorage (for cross-tab sync)
    syncLoginFlag: (state) => {
      if (typeof window !== 'undefined') {
        const userLoggedIn = localStorage.getItem('user_logged_in') === 'true';
        state.user_logged_in = userLoggedIn;
      }
    },
    // Action to update is_active status (from account deactivation/activation)
    updateIsActive: (state, action) => {
      const isActive = action.payload.is_active;
      state.is_active = isActive;
      if (state.user) {
        state.user.is_active = isActive;
        // Update localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(state.user));
          localStorage.setItem('is_active', isActive === "1" || isActive === 1 ? 'true' : 'false');
        }
      }
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  setUserType,
  updateUserProfile,
  setAuthLoading,
  clearAuthError,
  logout,
  emailVerified,
  mobileVerified,
  updateOnlineStatus,
  updateAvailability,
  restoreAuthState,
  updateSlug,
  setBadge,
  syncLoginFlag,
  updateIsActive
} = authSlice.actions;

export default authSlice.reducer;